function bindFlourishSidebarHandles(root = document) {
  root.querySelectorAll('[data-js-sidebar-handle]').forEach((elm) => {
    if (elm.dataset.flourishSidebarBound === 'true') return;
    if (!elm.hasAttribute('aria-controls')) return;

    const sidebar = document.getElementById(elm.getAttribute('aria-controls'));
    if (!sidebar || typeof sidebar.show !== 'function') return;

    elm.dataset.flourishSidebarBound = 'true';

    elm.addEventListener('click', (event) => {
      event.preventDefault();
      elm.setAttribute('aria-expanded', 'true');
      sidebar.show();
    });

    elm.addEventListener('keyup', (event) => {
      if (event.keyCode !== window.KEYCODES?.RETURN) return;
      event.preventDefault();
      elm.setAttribute('aria-expanded', 'true');
      sidebar.show();
      window.lastFocusedElm = elm;
      sidebar.querySelector('[data-js-close]')?.focus();
    });
  });
}

function bindFlourishSiteOverlay() {
  if (window.flourishSiteOverlayBound) return;
  const overlay = document.querySelector('.site-overlay');
  if (!overlay) return;

  window.flourishSiteOverlayBound = true;
  overlay.addEventListener('click', () => {
    document.querySelector('.sidebar--opened')?.hide();
  });
}

function lockFlourishHeaderBarHeight(header, force = false) {
  if (!header || (header.dataset.barHeightLocked === 'true' && !force)) return;

  const bar = header.querySelector('.flourish-header__bar');
  if (!bar) return;

  const barHeight = Math.round(bar.getBoundingClientRect().height);
  if (barHeight <= 0) return;

  document.documentElement.style.setProperty('--flourish-header-bar-height', `${barHeight}px`);
  header.dataset.barHeightLocked = 'true';
}

function syncFlourishAnnouncementBarOffset() {
  const announcement = document.querySelector('header .mount-announcement-bar announcement-bar');
  const header = document.querySelector('flourish-header');

  if (!announcement) {
    document.documentElement.style.setProperty('--flourish-announcement-bar-height', '0px');
    document.documentElement.style.setProperty('--flourish-announcement-bar-offset', '0px');
    document.body.classList.remove('has-announcement-bar');
    return;
  }

  document.body.classList.add('has-announcement-bar');

  const height = Math.round(announcement.getBoundingClientRect().height);
  document.documentElement.style.setProperty('--flourish-announcement-bar-height', `${height}px`);

  const { bottom } = announcement.getBoundingClientRect();
  const offset = bottom > 0 ? Math.round(bottom) : 0;
  document.documentElement.style.setProperty('--flourish-announcement-bar-offset', `${offset}px`);

  lockFlourishHeaderBarHeight(header);
}

if (typeof FlourishHeader !== 'function') {
  class FlourishHeader extends HTMLElement {
    connectedCallback() {
      this._mega = this.querySelector('[data-flourish-mega]');
      this._megaToggle = this.querySelector('[data-flourish-mega-toggle]');
      this._searchPanel = this.querySelector('[data-flourish-search]');
      this._searchToggle = this.querySelectorAll('[data-flourish-search-toggle]');
      this._searchInput = this.querySelector('[data-js-search-input]');
      this._scrollRaf = null;
      this._placeholder = null;
      this._stickyThreshold = 0;
      this._isAbsolute = this.classList.contains('flourish-header--absolute');

      this._onDocumentClick = this._onDocumentClick.bind(this);
      this._onDocumentKeydown = this._onDocumentKeydown.bind(this);
      this._onMegaMouseEnter = this._onMegaMouseEnter.bind(this);
      this._onMegaMouseLeave = this._onMegaMouseLeave.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);

      bindFlourishSidebarHandles(this);
      bindFlourishSidebarHandles(document);
      bindFlourishSiteOverlay();
      syncFlourishAnnouncementBarOffset();
      requestAnimationFrame(() => {
        lockFlourishHeaderBarHeight(this);
      });

      if (this.hasAttribute('data-sticky-header')) {
        this._measureStickyThreshold();
        window.addEventListener('resize', this._onResize, { passive: true });
      }

      this._updateScrollState();
      window.addEventListener('scroll', this._onScroll, { passive: true });

      if (this._megaToggle && this._mega) {
        this._megaToggle.addEventListener('mouseenter', this._onMegaMouseEnter);
        this.addEventListener('mouseleave', this._onMegaMouseLeave);
      }

      this._searchToggle.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this._toggleSearch();
        });
      });

      document.addEventListener('click', this._onDocumentClick);
      document.addEventListener('keydown', this._onDocumentKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocumentClick);
      document.removeEventListener('keydown', this._onDocumentKeydown);
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      this.removeEventListener('mouseleave', this._onMegaMouseLeave);
      this._removeStickyPlaceholder();

      if (this._scrollRaf) {
        cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = null;
      }
    }

    _onResize() {
      syncFlourishAnnouncementBarOffset();
      if (!this.hasAttribute('data-sticky-header')) return;
      this._measureStickyThreshold();
      this._updateScrollState();
    }

    _measureStickyThreshold() {
      const wasFixed = this.classList.contains('is-sticky-fixed');

      if (wasFixed) {
        this.classList.remove('is-sticky-fixed');
        this._removeStickyPlaceholder();
      }

      if (this._isAbsolute) {
        this._stickyThreshold = 8;
      } else {
        this._stickyThreshold = Math.max(0, Math.round(this.getBoundingClientRect().top + window.scrollY));
      }

      if (wasFixed) {
        this._updateScrollState();
      }
    }

    _onScroll() {
      if (this._scrollRaf) return;

      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null;
        this._updateScrollState();
      });
    }

    _updateScrollState() {
      syncFlourishAnnouncementBarOffset();

      const scrolled = window.scrollY > 8;
      this.classList.toggle('is-scrolled', scrolled);

      if (!this.hasAttribute('data-sticky-header')) return;

      const shouldFix = window.scrollY >= this._stickyThreshold;

      if (shouldFix) {
        if (!this.classList.contains('is-sticky-fixed')) {
          lockFlourishHeaderBarHeight(this);
          this.classList.add('is-sticky-fixed');
          if (!this._isAbsolute) {
            this._createStickyPlaceholder();
          }
        } else if (this._placeholder) {
          const barHeight = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--flourish-header-bar-height'),
            10
          );
          this._placeholder.style.height = `${barHeight > 0 ? barHeight : this.offsetHeight}px`;
        }
      } else if (this.classList.contains('is-sticky-fixed')) {
        this.classList.remove('is-sticky-fixed');
        this._removeStickyPlaceholder();
      }
    }

    _createStickyPlaceholder() {
      if (this._placeholder) return;

      lockFlourishHeaderBarHeight(this);

      const barHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--flourish-header-bar-height'),
        10
      );

      this._placeholder = document.createElement('div');
      this._placeholder.className = 'flourish-header__sticky-placeholder';
      this._placeholder.style.height = `${barHeight > 0 ? barHeight : this.offsetHeight}px`;
      this._placeholder.setAttribute('aria-hidden', 'true');
      this.before(this._placeholder);
    }

    _removeStickyPlaceholder() {
      this._placeholder?.remove();
      this._placeholder = null;
    }

    _onMegaMouseEnter() {
      if (window.matchMedia('(min-width: 1024px)').matches) {
        this._openMega();
      }
    }

    _onMegaMouseLeave(event) {
      if (!window.matchMedia('(min-width: 1024px)').matches) return;
      if (this.contains(event.relatedTarget)) return;
      this._closeMega();
    }

    _openMega() {
      if (!this._mega || !this._megaToggle) return;
      this._mega.hidden = false;
      this._megaToggle.setAttribute('aria-expanded', 'true');
      this._megaToggle.classList.add('is-active');
      this.classList.add('is-mega-open');
    }

    _closeMega() {
      if (!this._mega || !this._megaToggle) return;
      this._mega.hidden = true;
      this._megaToggle.setAttribute('aria-expanded', 'false');
      this._megaToggle.classList.remove('is-active');
      this.classList.remove('is-mega-open');
    }

    _toggleSearch() {
      if (!this._searchPanel) return;
      const isOpen = !this._searchPanel.hidden;
      this._searchPanel.hidden = isOpen;
      this.classList.toggle('is-search-open', !isOpen);
      this._searchToggle.forEach((button) => {
        button.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });
      if (!isOpen) {
        this._closeMega();
        window.setTimeout(() => this._searchInput?.focus(), 50);
      }
    }

    _closeSearch() {
      if (!this._searchPanel) return;
      this._searchPanel.hidden = true;
      this.classList.remove('is-search-open');
      this._searchToggle.forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });
    }

    _onDocumentClick(event) {
      if (this._mega && !this.contains(event.target)) {
        this._closeMega();
      }
      if (this._searchPanel && !this._searchPanel.hidden && !this.contains(event.target)) {
        this._closeSearch();
      }
    }

    _onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this._closeMega();
        this._closeSearch();
      }
    }
  }

  if (typeof customElements.get('flourish-header') === 'undefined') {
    customElements.define('flourish-header', FlourishHeader);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  bindFlourishSidebarHandles();
  bindFlourishSiteOverlay();
  syncFlourishAnnouncementBarOffset();
  window.addEventListener('resize', syncFlourishAnnouncementBarOffset, { passive: true });

  const announcement = document.querySelector('header .mount-announcement-bar announcement-bar');
  if (announcement && 'ResizeObserver' in window) {
    new ResizeObserver(syncFlourishAnnouncementBarOffset).observe(announcement);
  }
});

window.addEventListener('load', () => {
  const header = document.querySelector('flourish-header');
  lockFlourishHeaderBarHeight(header, true);
  syncFlourishAnnouncementBarOffset();
});
